import React, { useEffect, useState } from "react";
import {
  getTenantById,
  getTenantOverview,
} from "../../../../../shared/services/apis";
import { FaMapMarkerAlt, FaLinkedin, FaTwitter } from "react-icons/fa";
import saveAs from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document as DocxDocument,
  Packer,
  Paragraph,
  ImageRun,
  Footer,
  AlignmentType,
} from "docx";
import { RiDownloadLine } from "react-icons/ri";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

interface AboutProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export const About: React.FC<AboutProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  interface PhoneNumbers {
    Primary: string;
    Sanitized: string;
  }

  interface CompanyOwnershipDetails {
    OwnedBy?: string | null;
    LatestFundingRound?: string;
    LatestFundingRoundDate?: string;
    Investors?: string[];
    PubliclyTraded: boolean;
  }

  interface CompanyBasedDetails {
    Headquarters: string;
    FoundedYear: number;
    EstimatedEmployees: number;
    AnnualRevenueCAD: number;
    PrimaryDomain: string;
    Industry: string;
    SICCode: string;
    NAICSCode: string;
  }

  interface Employee {
    EstimatedCount: number;
    CoveringDepartments: string[];
  }

  interface CompanySocialMediaLinks {
    Twitter?: string | null;
    LinkedIn?: string | null;
    Facebook?: string | null;
  }

  interface TenantAboutData {
    Name: string;
    Address: string;
    PhoneNumbers: PhoneNumbers;
    CompanyOwnershipDetails: CompanyOwnershipDetails;
    CompanyBasedDetails: CompanyBasedDetails;
    Employee: Employee;
    CompanySocialMediaLinks: CompanySocialMediaLinks;
    Description: string;
    PrimaryIndustry: string;
    NAICSCodes: string[];
    Specialties: string[];
  }

  const [data, setData] = useState<TenantAboutData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloadingPDF, setDownloadingPDF] = useState<boolean>(false);
  const [downloadingWord, setDownloadingWord] = useState<boolean>(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      try {
        const response = await getTenantOverview(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (error) {
        console.error("Error fetching tenant overview:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };
    fetchTenant();
  }, [tenantId]);

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Load watermark image helper function
      const loadImage = (url: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });

      // Load watermark image
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch {
          console.warn("Watermark image failed to load");
        }
      }

      // Title centered
      doc.setFontSize(22);
      doc.setTextColor("#003366");
      doc.text(data?.Name || "Company", pageWidth / 2, 20, { align: "center" });

      // Description heading
      doc.setFontSize(16);
      doc.setTextColor("#000000");
      doc.text("Description", 14, 40);

      // Description content wrapped inside page width
      doc.setFontSize(12);
      doc.setTextColor("#444444");
      const descriptionLines = doc.splitTextToSize(
        data?.Description || "",
        pageWidth - 28
      );
      doc.text(descriptionLines, 14, 48);

      let currentY = 48 + descriptionLines.length * 7 + 10;

      // Specialties table
      if (data?.Specialties?.length) {
        doc.setFontSize(16);
        doc.setTextColor("#000000");
        doc.text("Specialties", 14, currentY);
        currentY += 8;

        autoTable(doc, {
          startY: currentY,
          theme: "grid",
          head: [["Specialties"]],
          headStyles: { fillColor: "#003366", textColor: "#FFFFFF" },
          body: data.Specialties.map((s) => [s]),
          margin: { left: 14, right: 14 },
          styles: { fontSize: 12, textColor: "#444444" },
        });

        const specialTable = (doc as any).lastAutoTable;
        if (specialTable && specialTable.finalY) {
          currentY = specialTable.finalY + 10;
        }
      }

      // Add watermark to every page
      const addWatermark = (pageNum: number) => {
        if (!watermarkImg) return;
        doc.setPage(pageNum);
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 }));
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );
        doc.restoreGraphicsState();
      };

      // Footer with page number and logo image
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      doc.save(`About_${data?.Name || "company"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(`Failed to generate PDF: ${error}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);
    try {
      // Helper to fetch image and convert to Uint8Array
      const fetchImageAsUint8Array = async (
        url: string
      ): Promise<Uint8Array> => {
        const response = await fetch(url);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        return new Uint8Array(arrayBuffer);
      };

      // Load and convert logo image
      let logoImageData: Uint8Array | null = null;
      let mimeType: "png" | "jpg" | "gif" | "bmp" = "png"; // default mimeType

      if (tenant?.logoUrl) {
        try {
          logoImageData = await fetchImageAsUint8Array(tenant.logoUrl);
          // Optional: infer mimeType from URL extension or response.headers
          if (
            tenant.logoUrl.endsWith(".jpg") ||
            tenant.logoUrl.endsWith(".jpeg")
          )
            mimeType = "jpg";
          else if (tenant.logoUrl.endsWith(".gif")) mimeType = "gif";
          else if (tenant.logoUrl.endsWith(".bmp")) mimeType = "bmp";
        } catch (error) {
          console.warn("Failed to load logo image data:", error);
        }
      }

      // Prepare specialty paragraphs
      const specialtyParagraphs = (data?.Specialties || []).map(
        (spec) =>
          new Paragraph({
            text: spec,
            bullet: { level: 0 },
            spacing: { after: 100 },
          })
      );

      // Prepare footer image with ImageRun only if image data loaded
      const footerChildren: Paragraph[] = [];
      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: mimeType,
                data: logoImageData,
                transformation: { width: 60, height: 60 },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      // Create the document
      const doc = new DocxDocument({
        sections: [
          {
            children: [
              new Paragraph({
                text: data?.Name || "Company",
                heading: "Heading1",
                spacing: { after: 300 },
              }),
              new Paragraph({
                text: "Description",
                heading: "Heading2",
                spacing: { before: 300, after: 200 },
              }),
              new Paragraph({
                text: data?.Description || "",
                spacing: { after: 300 },
              }),
              ...(specialtyParagraphs.length > 0
                ? [
                    new Paragraph({
                      text: "Specialties",
                      heading: "Heading2",
                      spacing: { before: 300, after: 200 },
                    }),
                    ...specialtyParagraphs,
                  ]
                : []),
            ],
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `About_${data?.Name || "company"}.docx`);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 mt-10 max-w-4xl mx-auto mb-30 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-6 border-b border-gray-300 pb-6">
          <div className="flex-1 mr-10 space-y-3">
            <div className="h-6 bg-gray-200 rounded w-2/3" />
            <div className="flex items-center gap-2">
              <div className="h-4 bg-gray-200 rounded w-5" />
              <div className="h-4 bg-gray-200 rounded w-1/3" />
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="h-4 bg-gray-200 rounded w-28" />
            </div>
            <div className="flex gap-6 mt-4">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
          <div className="h-10 w-32 bg-gray-200 rounded-md" />
        </div>

        {/* Description Skeleton */}
        <div className="mb-5">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-5/6" />
            <div className="h-4 bg-gray-100 rounded w-2/3" />
          </div>
        </div>

        {/* Industry Details Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm mb-6">
          {[1, 2, 3].map((_, idx) => (
            <div key={idx}>
              <div className="h-4 bg-gray-200 rounded w-28 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-20" />
            </div>
          ))}
        </div>

        {/* Specialties Skeleton */}
        <div className="mb-4">
          <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
          <div className="flex flex-wrap gap-3">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="h-6 bg-gray-100 rounded-full w-24" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-red-500">Failed to load data.</div>;
  }

  return (
    <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 max-w-4xl mt-0 2xl:mt-20 mx-auto ">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-6 border-b border-gray-300 pb-6 ">
        <div className="flex-1 mr-10">
          <h2 className="text-2xl text-gray-900 capitalize mb-3">
            {data.Name}
          </h2>
          <div className="text-sm text-gray-600 flex items-center mb-3">
            <FaMapMarkerAlt className="text-red-500 mr-1" />
            <span>
              {data.CompanyBasedDetails.Headquarters || data.Address || "N/A"}
            </span>
          </div>

          <div className="text-sm text-gray-600 flex items-center justify-between flex-wrap gap-2 pr-10">
            <span>
              {" "}
              {data.CompanyOwnershipDetails.PubliclyTraded
                ? "Privately held"
                : "Publically held"}
            </span>
            <span>
              Founded: {data.CompanyBasedDetails.FoundedYear || "N/A"}
            </span>
            <span>
              {data.CompanyBasedDetails.EstimatedEmployees ||
                data.Employee.EstimatedCount ||
                "N/A"}{" "}
              Employees{" "}
            </span>
          </div>
          {/* {Boolean(data.CompanyBasedDetails.AnnualRevenueCAD) && (
            <div className="text-sm text-gray-600 mt-2">
              <span>Annual Revenue: ${data.CompanyBasedDetails.AnnualRevenueCAD.toLocaleString()} CAD</span>
            </div>
          )} */}

          <div className="flex gap-8 mt-4 text-sm text-blue-800">
            {data.PhoneNumbers?.Primary && (
              <a
                href={`tel:${data.PhoneNumbers.Sanitized}`}
                className="flex items-center gap-1 hover:underline"
              >
                {data.PhoneNumbers.Primary}
              </a>
            )}
            {data.CompanySocialMediaLinks?.LinkedIn && (
              <a
                href={data.CompanySocialMediaLinks.LinkedIn}
                className="flex items-center gap-1 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                <FaLinkedin /> LinkedIn
              </a>
            )}
            {data.CompanySocialMediaLinks?.Twitter && (
              <a
                href={data.CompanySocialMediaLinks.Twitter}
                className="flex items-center gap-1 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                <FaTwitter /> Twitter
              </a>
            )}
          </div>
        </div>

        {data.CompanyBasedDetails?.PrimaryDomain && (
          <div>
            <a
              href={`https://${data.CompanyBasedDetails.PrimaryDomain}`}
              className="bg-gray-100 px-4 py-2  text-sm rounded-md font-medium text-gray-800 hover:bg-gray-200 transition"
              target="_blank"
              rel="noreferrer"
            >
              Visit Website ↗
            </a>
            <div className="flex gap-2  justify-end mt-8">
              <button
                onClick={downloadPDF}
                disabled={downloadingPDF}
                className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <RiDownloadLine className="w-4 h-4" />
                {downloadingPDF ? "Generating..." : "PDF"}
              </button>
              <button
                onClick={downloadWord}
                disabled={downloadingWord}
                className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <RiDownloadLine className="w-4 h-4" />
                {downloadingWord ? "Generating..." : "Word"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {data.Description && (
        <div className="mb-5">
          <h3 className="text-md font-light text-gray-500 mb-2">Description</h3>
          <p className="text-sm text-gray-800 leading-relaxed">
            {data.Description}
          </p>
        </div>
      )}

      {/* Industry Details */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm mb-6">
        <div>
          <p className="text-gray-500 font-medium mb-1">Primary Industry</p>
          <p className="text-gray-800 capitalize">
            {data.PrimaryIndustry || data.CompanyBasedDetails.Industry || "N/A"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 font-medium mb-1">SIC Code</p>
          <p className="text-gray-800">
            {data.CompanyBasedDetails.SICCode || "N/A"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 font-medium mb-1">NAICS Code</p>
          <p className="text-blue-800 cursor-pointer">
            {data.CompanyBasedDetails.NAICSCode ||
              data.NAICSCodes?.[0] ||
              "N/A"}
          </p>
        </div>
      </div>

      {/* Specialties */}
      {data.Specialties?.length > 0 && (
        <div>
          <p className="text-gray-500 font-medium text-sm mb-2">Specialties</p>
          <div className="flex flex-wrap gap-3">
            {data.Specialties.slice(0, 12).map((item) => (
              <span
                key={item}
                className="bg-gray-100 capitalize text-sm px-3 py-1 rounded-full text-gray-700"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Funding & Ownership */}
      {/* {(data.CompanyOwnershipDetails.LatestFundingRound || (data.CompanyOwnershipDetails.Investors && data.CompanyOwnershipDetails.Investors.length > 0)) && (
        <div className="mt-6">
          <h3 className="text-md font-light text-gray-500 mb-2">Funding & Ownership</h3>
          <div className="text-sm text-gray-800 space-y-1">
            {data.CompanyOwnershipDetails.OwnedBy && (
              <p><strong>Owned By:</strong> {data.CompanyOwnershipDetails.OwnedBy}</p>
            )}
            {data.CompanyOwnershipDetails.LatestFundingRound && (
              <p><strong>Latest Funding Round:</strong> {data.CompanyOwnershipDetails.LatestFundingRound}</p>
            )}
            {data.CompanyOwnershipDetails.LatestFundingRoundDate && (
              <p><strong>Funding Date:</strong> {new Date(data.CompanyOwnershipDetails.LatestFundingRoundDate).toLocaleDateString()}</p>
            )}
            {data.CompanyOwnershipDetails.Investors && data.CompanyOwnershipDetails.Investors.length > 0 && (
              <p><strong>Investors:</strong> {data.CompanyOwnershipDetails.Investors.join(", ")}</p>
            )}
          </div>
        </div>
      )} */}

      {/* Employee Information */}
      {/* {data.Employee?.CoveringDepartments?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-md font-light text-gray-500 mb-2">Departments</h3>
          <div className="flex flex-wrap gap-2">
            {data.Employee.CoveringDepartments.map((dept) => (
              <span
                key={dept}
                className="bg-blue-100 text-sm px-3 py-1 rounded-full text-blue-700 capitalize"
              >
                {dept.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )} */}
    </div>
  );
};
